import { RowSelectionState } from '@tanstack/react-table'
import FbButton from 'components/FbUI/FbButton'
import { FbModal } from 'components/UI/Modal'
import { Download, Save } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { PATHS } from 'paths'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DebugTable } from './DebugTable'
import { useImporterViewModel } from '../ImporterViewModel'
import { ImportWizardConfirmMatchedStep } from './ConfirmMatchedStep'
import { ImportWizardFinishMatchingStep } from './FinishMatchingStep'
import { ImportWizardDialogConfirmDeselectMatched } from './DialogConfirmDeselectMatched'
import { ImportWizardReviewPendingStep } from './ReviewPendingStep'
import { ImportWizardStartImportStep } from './StartImportStep'
import { CSVRow, InternalColumns } from '../importer-types'
import { WizardStep } from './types'
import { useWizard } from '../Importer'

export function ImportWizard() {
  const viewModel = useImporterViewModel()
  const wizard = useWizard()
  const navigate = useNavigate()

  // ================= UI State ======================
  const [showDebug, setShowDebug] = useState(false)

  // Step 1: Track which rows user wants to deselect (local UI state)
  const [deselectedRows, setDeselectedRows] = useState<RowSelectionState>({})

  // ================= Reset state on data refresh ======================
  const prevDataVersion = useRef(wizard.dataVersion)
  useEffect(() => {
    if (wizard.dataVersion !== prevDataVersion.current) {
      setDeselectedRows({})
      prevDataVersion.current = wizard.dataVersion
    }
  }, [wizard.dataVersion])

  // ================= Derived Values ======================
  const rowsToBeRemovedFromReadyState = useMemo<CSVRow[]>(() => {
    return (
      viewModel.companiesPrematched.filter(
        (row: CSVRow) => deselectedRows[row[InternalColumns.COMPANY_HASH]]
      ) ?? []
    )
  }, [deselectedRows, viewModel.companiesPrematched])

  const hasDeselectedRows = Object.values(deselectedRows).some(
    (v) => v === true
  )

  // ================= Handlers ======================

  /**
   * Step-aware "Next" button. The machine decides what to do based on the
   * current step. Component just passes current data and deselection info.
   */
  const handleNext = useCallback(() => {
    const deselectedHashes = hasDeselectedRows
      ? Object.keys(deselectedRows).filter((k) => deselectedRows[k])
      : undefined
    wizard.next(viewModel.importData ?? undefined, deselectedHashes)
  }, [wizard, viewModel.importData, deselectedRows, hasDeselectedRows])

  // ================= Step 4 Handlers ======================
  // Machine handles the full lifecycle: call API → poll → refresh.
  // Component only sends a single event. For deals, pre-flight validation
  // (which may open modals) still runs in the component.
  const handleImportTags = useCallback(() => {
    wizard.importTags()
  }, [wizard])

  const handleImportGpos = useCallback(() => {
    wizard.importGpos()
  }, [wizard])

  const handleImportDeals = useCallback(() => {
    wizard.importDeals()
  }, [wizard])

  const handleImportContacts = useCallback(() => {
    wizard.importContacts()
  }, [wizard])

  const handleImportNotes = useCallback(() => {
    wizard.importNotes()
  }, [wizard])

  // ================= Save Handler ======================
  // Save using the machine's unified OPERATING flow.
  // prepareDataForPersist() in OPERATING.PREPARING will mark matched companies as READY.
  const handleSaveClick = useCallback(() => {
    if (!viewModel.importData) return
    wizard.save(viewModel.importData)
  }, [viewModel.importData, wizard])

  // ================= Prevent navigation when importing ======================
  useEffect(() => {
    const isImporting = wizard.isWaitingForProcess || wizard.isOperating

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isImporting) {
        e.preventDefault()
        e.returnValue = ''
        return ''
      }
    }

    const handlePopState = (e: PopStateEvent) => {
      if (isImporting) {
        e.preventDefault()
        const confirmLeave = window.confirm(
          'Import is in progress. Are you sure you want to leave? This may cause data loss.'
        )
        if (!confirmLeave) {
          window.history.pushState(null, '', window.location.href)
        }
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('popstate', handlePopState)

    // Push state to enable popstate detection
    if (isImporting) {
      window.history.pushState(null, '', window.location.href)
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('popstate', handlePopState)
    }
  }, [wizard.isWaitingForProcess, wizard.isOperating])

  // ================= Loading State ======================
  // Loading comes entirely from the machine — no local loading state needed
  // Fix saves now go through OPERATING (covered by wizard.isLoading),
  // so only validatingDeals needs a separate check for the wizard overlay.
  const isShowingLoading = wizard.isLoading || wizard.isValidatingDeals

  const loadingDisplayMessage =
    wizard.loadingMessage || 'Loading import data...'

  // ================= Render ======================
  return (
    <div className="flex flex-col overflow-hidden position-relative w-full h-full">
      {/* Loading Overlay */}
      {isShowingLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-90 z-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-fb-dark-green" />
            <div className="text-lg font-semibold text-gray-900">
              {loadingDisplayMessage}
            </div>
            <div className="text-sm text-gray-600">
              Please do not close this window
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="border-b pb-2 mb-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-bold text-gray-900">
                {wizard.currentStep === WizardStep.STEP3_DECIDE &&
                viewModel.skipMatching
                  ? 'Create Custom Companies'
                  : wizard.currentStep && stepTitles[wizard.currentStep].title}
              </h2>
              {wizard.importName && (
                <span className="text-sm text-gray-500 font-normal">
                  — {wizard.importName}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-1">
              {wizard.currentStep === WizardStep.STEP3_DECIDE &&
              viewModel.skipMatching
                ? 'All companies will be created as custom (user-generated) companies. Select the company type category and review before proceeding.'
                : wizard.currentStep &&
                  stepTitles[wizard.currentStep].description}
            </p>
          </div>
          <div className="flex gap-2">
            <FbButton
              type="button"
              onClick={handleSaveClick}
              variant="primary"
              disabled={
                !viewModel.hasPendingChanges || wizard.isOperatingSaving
              }
              loading={wizard.isOperatingSaving}
            >
              <Save size={16} />
              Save
            </FbButton>
            <FbButton
              type="button"
              onClick={() => viewModel.setShowConfigureExportModal(true)}
              variant="secondary"
            >
              <Download size={16} />
              Export
            </FbButton>
            <FbButton
              type="button"
              onClick={() => setShowDebug(!showDebug)}
              variant="secondary"
              className="hidden"
            >
              {showDebug ? 'Hide Debug' : 'Show Debug'}
            </FbButton>
            <FbButton
              variant="secondary"
              onClick={() => navigate(PATHS.IMPORT_WIZARD)}
            >
              Go Back to List
            </FbButton>
          </div>
        </div>
      </div>

      {/* Content - height constrained, steps handle their own scrolling */}
      <div className="flex-1 min-h-0">
        {showDebug ? (
          <DebugTable data={viewModel.importData?.rows ?? []} />
        ) : (
          <>
            {/* Step 1: Confirm Matched */}
            {wizard.matches(WizardStep.STEP1_CONFIRM) && (
              <ImportWizardConfirmMatchedStep
                loading={wizard.isLoading}
                show={!isShowingLoading}
                rowSelection={deselectedRows}
                setRowSelection={setDeselectedRows}
                headers={viewModel.wizardHeaders}
                data={viewModel.companiesPrematched}
              />
            )}

            {/* Step 1 Modal: Deselect Confirmation */}
            <ImportWizardDialogConfirmDeselectMatched
              show={wizard.isDeselectModalOpen && !isShowingLoading}
              headers={viewModel.wizardHeaders}
              data={rowsToBeRemovedFromReadyState}
              onCancel={wizard.cancelDeselect}
              onAccept={wizard.confirmDeselect}
            />

            {/* Step 2: Review Pending */}
            {wizard.matches(WizardStep.STEP2_REVIEW) && (
              <ImportWizardReviewPendingStep
                show={!isShowingLoading}
                data={viewModel.companiesPendingReview}
              />
            )}

            {/* Step 3: Decide Unmatched */}
            {wizard.matches(WizardStep.STEP3_DECIDE) && (
              <ImportWizardFinishMatchingStep
                show={!isShowingLoading && !wizard.isConfirmModalOpen}
                data={viewModel.allCompanies}
                skipMatching={viewModel.skipMatching}
              />
            )}

            {/* Step 3 Modal: Confirm Matches */}
            <ConfirmMatchesModal
              show={wizard.isConfirmModalOpen}
              onCancel={wizard.cancelConfirm}
              onConfirm={wizard.confirmMatches}
              amountCustomCompanies={viewModel.importCounts.custom.total}
            />

            {/* Step 4: Import */}
            {wizard.matches(WizardStep.STEP4_IMPORT) && (
              <ImportWizardStartImportStep
                show={!isShowingLoading}
                onImportTags={handleImportTags}
                onImportGpos={handleImportGpos}
                onImportDeals={handleImportDeals}
                onImportContacts={handleImportContacts}
                onImportNotes={handleImportNotes}
                isProcessing={wizard.isOperating}
                currentOperation={wizard.operation}
              />
            )}
          </>
        )}
      </div>

      {/* Footer with stats and buttons */}
      <div className="border-t pt-4 mt-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex gap-4 text-sm text-gray-600">
            <div>
              Matched:{' '}
              {viewModel.allCompanies.length -
                viewModel.amountCompaniesLeftUnmatched}
            </div>
            <div>Unmatched: {viewModel.amountCompaniesLeftUnmatched}</div>
          </div>

          <div className="flex gap-2">
            {wizard.canGoBack &&
              !(
                wizard.currentStep === WizardStep.STEP4_IMPORT &&
                viewModel.areAllRowsSet
              ) && (
                <FbButton
                  type="button"
                  onClick={() => wizard.navigateBack()}
                  variant="secondary"
                >
                  {wizard.currentStep &&
                    stepButtonTexts[wizard.currentStep].back}
                </FbButton>
              )}
            {wizard.currentStep &&
              wizard.currentStep !== WizardStep.STEP4_IMPORT && (
                <FbButton type="button" onClick={() => handleNext()}>
                  {wizard.currentStep === WizardStep.STEP3_DECIDE &&
                  viewModel.skipMatching
                    ? 'Create Custom Companies'
                    : wizard.currentStep &&
                      stepButtonTexts[wizard.currentStep].next}
                </FbButton>
              )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ================= Step Metadata ======================
const stepTitles: Record<WizardStep, { title: string; description: string }> = {
  [WizardStep.STEP1_CONFIRM]: {
    title: 'Confirm Matches',
    description:
      'These are high-confidence matches. Review them and deselect any that look incorrect.',
  },
  [WizardStep.STEP2_REVIEW]: {
    title: 'Review Pending Matches',
    description: 'Review the pending data',
  },
  [WizardStep.STEP3_DECIDE]: {
    title: 'Confirm Matching',
    description:
      'Review the matching and confirm to move on to importing CRM data.',
  },
  [WizardStep.STEP4_IMPORT]: {
    title: 'CRM Data Import Summary',
    description:
      'Review the CRM data (Tags, Deals, Contacts, Company Notes) that will be imported',
  },
}

const stepButtonTexts: Record<WizardStep, { back: string; next: string }> = {
  [WizardStep.STEP1_CONFIRM]: { back: 'Back', next: 'Next' },
  [WizardStep.STEP2_REVIEW]: { back: 'Back', next: 'Finish Review' },
  [WizardStep.STEP3_DECIDE]: { back: 'Back', next: 'Next' },
  [WizardStep.STEP4_IMPORT]: { back: 'Back', next: 'Start Import' },
}

// ================= Confirm Matches Modal ======================
function ConfirmMatchesModal({
  show,
  onCancel,
  onConfirm,
  amountCustomCompanies,
}: {
  show: boolean
  onCancel: () => void
  onConfirm: () => void
  amountCustomCompanies: number
}) {
  let description = 'All selected matches will be confirmed.'
  if (amountCustomCompanies > 0) {
    description += ` ${amountCustomCompanies} custom ${amountCustomCompanies === 1 ? 'company' : 'companies'} will be created.`
  }

  return (
    <FbModal
      open={show}
      onOpenChange={(open) => !open && onCancel()}
      title="Confirm Your Matches"
      description={description}
      cancelButtonText="Cancel"
      acceptButtonText="Confirm"
      onCancel={onCancel}
      onAccept={onConfirm}
    />
  )
}
